import React, { useEffect, useRef, useState } from 'react';
import ProgressSpinner from '@avtechsoftware/react-component-library/src/components/common/ProgressSpinner';
import PropTypes from 'prop-types';
import { faPaperPlaneTop } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  NOTIFICATION_LEVEL_ERROR,
} from '@avtechsoftware/ra-javascript-utilities/src/types/NotificationLevels';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import Comment from './Comment';
import {
  addNewComment, clearConversationSlice,
  fetchComments,
  selectAlertCommentList,
  selectConversationHasNextPage,
  selectConversationTotalNumRecords,
  selectIsLoadingConversation, setFetchTimestamp,
  updateActiveViewAlertComment,
} from '../../../redux/slices/conversation/conversationSlice';
import { selectSessionInformation } from '../../../redux/slices/sessionSlice';
import { AlertCommentType } from '../../../utility/types/AlertCommentType';
import { connectModalContainerContextToComponent } from '../../../contexts/ModalContext/ModalContext';
import DeleteCommentModal from './DeleteCommentModal';
import styles from './AlertConversationsTabContent.module.css';
import {
  pushSessionMessage,
} from '../../../redux/slices/toastNotificationSlice/toastNotificationSlice';
import { RaaRoles, sessionUserHasRolePrivileges } from '../../../utility/SecurityRules';

function AlertConversationsTabContent({ setModalJSX }) {
  const dispatch = useDispatch();
  const bottomBoundaryRef = useRef(null);
  const comments = useSelector(selectAlertCommentList);
  const { userId, activeSubscription } = useSelector(selectSessionInformation);
  const loading = useSelector(selectIsLoadingConversation);
  const hasNextPage = useSelector(selectConversationHasNextPage);
  const totalRecords = useSelector(selectConversationTotalNumRecords);

  const [comment, setComment] = useState('');
  const [isSendingComment, setIsSendingComment] = useState(false);

  const handleDeleteSuccess = (commentId) => {
    const activeViewAlertComment = comments
      .find((latestComment) => latestComment.id === commentId);
    if (activeViewAlertComment) {
      dispatch(updateActiveViewAlertComment({
        commentId,
        comment: { ...activeViewAlertComment, type: AlertCommentType.DELETED },
      }));
    }
  };

  const handleSubmitComment = async () => {
    if (comment.length === 0) {
      return;
    }
    try {
      setIsSendingComment(true);
      setComment('');
      await dispatch(addNewComment({ text: comment, userId })).unwrap();
    } catch (e) {
      dispatch(pushSessionMessage({
        level: NOTIFICATION_LEVEL_ERROR,
        title: 'Unexpected error',
        body: 'Unexpected error sending comment.',
      }));
    } finally {
      setIsSendingComment(false);
    }
  };

  const handleOpenDeleteModal = (commentId) => {
    setModalJSX(<DeleteCommentModal
      commentId={commentId}
      handleDeleteSuccess={handleDeleteSuccess}
    />);
  };

  useEffect(() => {
    if (comments.length === 0) {
      dispatch(setFetchTimestamp(moment().unix()));
      dispatch(fetchComments());
    }
    return () => {
      dispatch(clearConversationSlice());
    };
  }, [dispatch]);

  useEffect(() => {
    const scrollObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.intersectionRatio > 0 && hasNextPage && !loading) {
            dispatch(fetchComments());
          }
        });
      },
    );
    const target = bottomBoundaryRef.current;
    scrollObserver.observe(target);
    return () => {
      if (target) {
        scrollObserver.unobserve(target);
      }
    };
  }, [bottomBoundaryRef, hasNextPage, dispatch, loading]);

  return (
    <>
      <div
        className={`border-bottom border-1 ${styles.commentsOuterContainer}`}
      >
        <div className={styles.commentsScrollContainer}>
          {(comments.sort((x, y) => (y.timestamp - x.timestamp)).map((msg) => (
            <React.Fragment key={msg.id}>
              <Comment
                isSystem={msg.type === AlertCommentType.ROOM_ALERT}
                isActiveUser={userId === msg.userId}
                handleOpenDeleteModal={() => handleOpenDeleteModal(msg.id)}
                commentedAt={msg.commentedAtTimestamp}
                userCanDelete={sessionUserHasRolePrivileges(
                  activeSubscription,
                  [RaaRoles.MANAGER, RaaRoles.ADMIN],
                )}
                {...msg}
              />
            </React.Fragment>
          )))}
          {loading && (
            <div className={styles.loadingSpinnerContainer}>
              <ProgressSpinner />
            </div>
          )}
          <div id="page-bottom-boundary" ref={bottomBoundaryRef} />
          <div
            style={{ height: `${(totalRecords - comments.length) * 200}px` }}
            className="placeholder"
          />
          <span className="text-center text-muted"><i>end of conversation</i></span>
        </div>
      </div>

      <div className="w-100 bg-light px-3 py-1">
        <form>
          <div className={styles.textInputContainer}>
            <textarea
              className={`${styles.textInputStyle} customized-text`}
              rows="2"
              name="alert-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Write a comment ..."
            />
            <button
              type="button"
              className={`btn btn-link ${styles.sendButtonStyle}`}
              onClick={handleSubmitComment}
              disabled={isSendingComment}
              id="add-alert-comment"
            >
              <FontAwesomeIcon icon={faPaperPlaneTop} />
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
AlertConversationsTabContent.propTypes = {
  setModalJSX: PropTypes.func.isRequired,
  alertId: PropTypes.number.isRequired,
};
export default connectModalContainerContextToComponent(AlertConversationsTabContent);
